import { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { FeatureLockedPage } from "./FeatureLockedPage";
import type { PlanTier } from "@/lib/tiers";

interface TierGateProps {
  requiredTier: PlanTier;
  feature: string;
  description: string;
  benefits?: string[];
  icon?: React.ComponentType<{ className?: string }>;
  children: ReactNode;
}

/**
 * Route-level tier gate. Renders children only if the user's effective tier
 * meets `requiredTier`; otherwise shows a full-page upgrade card.
 *
 * Use this as a wrapper around the page element in the router so the gated
 * page can use hooks freely without violating the Rules of Hooks.
 */
export function TierGate({
  requiredTier,
  feature,
  description,
  benefits,
  icon,
  children,
}: TierGateProps) {
  const { hasFeature, loading } = useSubscription();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hasFeature(requiredTier)) {
    return (
      <FeatureLockedPage
        icon={icon}
        feature={feature}
        requiredTier={requiredTier}
        description={description}
        benefits={benefits}
      />
    );
  }

  return <>{children}</>;
}
